import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { ReportConfig, Properties } from 'redux/reducers/reports'
import { ComponentsTypes } from 'components/Reports/enums'
import { handleGetDeepDiveFromExportedReports } from 'components/Reports/utils'

export interface DeepDiveResult {
  data: any
}

interface UseDeepDiveReportsOptions {
  reportConfig: ReportConfig | null
  enabled: boolean
  selectedReportRangeType?: { type: number; title: string } | null
  selectedUsersIds?: string[]
  tag?: any
  targetDataFilter?: any
  subRetailId?: string | null
  targetGroupId?: string
}

export const useDeepDiveReports = ({
  reportConfig,
  enabled,
  selectedReportRangeType,
  selectedUsersIds,
  tag,
  targetDataFilter,
  subRetailId,
  targetGroupId,
}: UseDeepDiveReportsOptions) => {
  const { retailConfig } = useSelector((state: RootState) => state.config)
  const { locale } = useSelector((state: RootState) => state.general)
  const { translations } = useSelector((state: RootState) => state.config)

  const [deepDiveResults, setDeepDiveResults] = useState<DeepDiveResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Extract exportable reports from reportConfig
  const getExportableReports = useCallback((config: ReportConfig): Properties[] => {
    const exportableReports: Properties[] = []

    const scanSubComponents = (subComponents: ReportConfig[]) => {
      subComponents.forEach((subComponent) => {
        if (subComponent.type === ComponentsTypes.rectangle && subComponent.properties?.is_exportable) {
          exportableReports.push(subComponent.properties)
        }
        if (subComponent.sub_components) {
          scanSubComponents(subComponent.sub_components)
        }
      })
    }

    if (config.sub_components) {
      scanSubComponents(config.sub_components)
    }

    return exportableReports
  }, [])

  // Fetch deep dive data for exportable reports
  const fetchDeepDiveData = useCallback(async () => {
    if (!enabled || !reportConfig || !targetGroupId) {
      return
    }

    const exportableReports = getExportableReports(reportConfig)
    if (exportableReports.length === 0) {
      setDeepDiveResults([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { preparedData, headersForExcell, textTitleArr } = await handleGetDeepDiveFromExportedReports({
        exportableReports,
        subRetailId: subRetailId ?? null,
        retailConfig,
        selectedReportRangeType: selectedReportRangeType ?? null,
        groupId: targetGroupId!,
        tag,
        targetDataFilter,
        selectedUsersIds: selectedUsersIds ?? null,
        locale,
        translations,
      })

      const combinedResults: DeepDiveResult[] = []

      textTitleArr.forEach((title, titleIndex) => {
        const headers = headersForExcell[titleIndex] || []
        const dataRows = preparedData[titleIndex] || []

        let formattedString = ''

        if (dataRows.length > 0) {
          formattedString += `${title}\n`
        }

        dataRows.forEach((row: any[]) => {
          const groupName = row[0]?.value || ''

          if (groupName) {
            formattedString += `${groupName}\n`

            headers.slice(1).forEach((header: any, headerIndex: number) => {
              const actualIndex = headerIndex + 1
              const value = row[actualIndex]?.value || ''
              const localeDict = (translations as any)?.[locale] || {}
              const headerText = localeDict[header.text_val] || header.text_val || ''

              if (headerText && value) {
                formattedString += `${headerText}: ${value},\n`
              }
            })

            formattedString += '\n'
          }
        })

        combinedResults.push({
          data: formattedString,
        })
      })

      setDeepDiveResults(combinedResults)
    } catch (err) {
      console.error('Error fetching deep dive reports:', err)
      setError('Failed to fetch deep dive reports')
    } finally {
      setIsLoading(false)
    }
  }, [
    enabled,
    reportConfig,
    targetGroupId,
    getExportableReports,
    subRetailId,
    retailConfig,
    selectedReportRangeType,
    tag,
    targetDataFilter,
    selectedUsersIds,
    locale,
    translations,
  ])

  useEffect(() => {
    if (enabled) {
      fetchDeepDiveData()
    } else {
      setDeepDiveResults([])
      setError(null)
    }
  }, [enabled, fetchDeepDiveData])
  return {
    deepDiveResults,
    isLoading,
    error,
    refetch: fetchDeepDiveData,
  }
}
